import axios from 'axios';
import { toast } from 'react-toastify';

import { mapSearchResultsArgsToApiQuery } from '~/api/graphql/search/mappers';

import { mapFavouritesResults, transformErrorResponse } from './mappers';
import { RestApi } from '../restApi';
import { buildQueryString } from '../utils';

import type {
  DeleteSitterProfileResponse,
  Favourite,
  GetFavoriteSittersRESTResponse,
  GetSimilarSittersRESTRequest,
  GetSimilarSittersRESTResponse,
  GetSitterAvailabilityRESTResponse,
  GetSitterDetailsRestResponse,
  GetSitterProfileRESTResponse,
  GetSitterWeekdaysAvailabilityRESTResponse,
  UpdateFavoriteSitterResponse,
  UpdateSitterHouseholdRequest,
  UpdateSitterHouseholdResponse,
  ValidateSitterAvailabilityRESTRequest,
} from './types';
import type { AxiosError, AxiosResponse } from 'axios';

export class SitterApi extends RestApi {
  static async deleteSitterProfile(): Promise<DeleteSitterProfileResponse | null> {
    try {
      const response: AxiosResponse<DeleteSitterProfileResponse> = await this.woofApi.delete(
        '/v1/sitters'
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);

      throw error;
    }
  }

  static async getFavoriteSitters(): Promise<Favourite[] | null> {
    try {
      const response: AxiosResponse<GetFavoriteSittersRESTResponse> = await this.woofApi.get(
        '/v1/sitters/liked'
      );

      return mapFavouritesResults(response.data);
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async getSimilarSitters(
    params: GetSimilarSittersRESTRequest
  ): Promise<GetSimilarSittersRESTResponse | null> {
    try {
      const { page, sitterProfileSlug, ...rest } = params;
      const { size, offset, ...searchQuery } = mapSearchResultsArgsToApiQuery(rest);

      const response: AxiosResponse<GetSimilarSittersRESTResponse> = await this.woofApi.post(
        `/v1/sitters/${sitterProfileSlug}/similar`,
        { page, ...searchQuery }
      );

      return { ...response.data };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async getSitterCalendarAvailabilty(
    sitterId: number,
    serviceTypeId: number | '',
    bookingType: string
  ): Promise<GetSitterAvailabilityRESTResponse['data']['unavailableDates'] | null> {
    try {
      const response: AxiosResponse<GetSitterAvailabilityRESTResponse> = await this.woofApi.get(
        `/v1/sitters/${sitterId}/calendar/availability?serviceTypeId=${serviceTypeId}&type=${bookingType}`
      );

      return [...response.data.data.unavailableDates];
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async getSitterCalendarWeekdaysAvailability(
    sitterId: number,
    startDate: string,
    serviceTypeId: number | ''
  ): Promise<GetSitterWeekdaysAvailabilityRESTResponse | null> {
    try {
      const queryString = buildQueryString({
        startDate,
        serviceTypeId,
      });

      /* eslint-disable max-len */
      const response: AxiosResponse<GetSitterWeekdaysAvailabilityRESTResponse> = await this.woofApi.get(
        `v1/sitters/${sitterId}/calendar/rwb/weekdays?${queryString}`
      );

      return { ...response.data };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async getSitterDetails(): Promise<GetSitterDetailsRestResponse['data'] | null> {
    try {
      const response: AxiosResponse<GetSitterDetailsRestResponse> = await this.woofApi.get(
        '/v1/sitters'
      );

      return { ...response.data.data };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async getSitterProfile(
    rewriteUrlOrId: string | number
  ): Promise<GetSitterProfileRESTResponse['data'] | null> {
    try {
      const response: AxiosResponse<GetSitterProfileRESTResponse> = await this.woofApi.get(
        `/v1/sitters/${rewriteUrlOrId}`
      );

      return { ...response.data.data };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async updateFavoriteSitter(
    id: number,
    isFavorite: boolean
  ): Promise<UpdateFavoriteSitterResponse | null> {
    try {
      const response: AxiosResponse<UpdateFavoriteSitterResponse> = await this.woofApi.post(
        `/v1/sitters/${id}/${isFavorite ? 'like' : 'unlike'}`
      );

      return { ...response.data };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async updateSitterHousehold(
    values: UpdateSitterHouseholdRequest
  ): Promise<UpdateSitterHouseholdResponse | null> {
    try {
      const response: AxiosResponse<UpdateSitterHouseholdResponse> = await this.woofApi.patch(
        '/v1/sitters/household',
        values
      );

      toast.success(response.data.message);

      return null;
    } catch (error) {
      toast.error('Could not update household details at this time');

      return this.handleError(error as AxiosError);
    }
  }

  static async validateSitterAvailability({
    sitterId,
    ...rest
  }: ValidateSitterAvailabilityRESTRequest): Promise<Record<string, string> | null> {
    try {
      await this.woofApi.post(`/v1/sitters/${sitterId}/calendar/validate`, rest);

      return null;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        return transformErrorResponse(error.response.data.errors);
      }

      return this.handleError(error as AxiosError);
    }
  }
}
